import posthog from 'posthog-js';
import { useAuthStore } from '../stores/authStore';
import type { ClientEvent, EventProps } from './analyticsEvents';
import { buildIdentifyPayload, identityKey, type IdentityUser } from './analyticsIdentity';

/**
 * PostHog wrapper. Every function is a no-op unless `VITE_POSTHOG_KEY` is set,
 * so local/dev builds make zero analytics network calls.
 *
 * This module may import the auth store; the store must never import this
 * module (keeps the dependency graph acyclic).
 */

let enabled = false;

function sendEmailEnabled(): boolean {
  const raw = import.meta.env.VITE_POSTHOG_SEND_EMAIL;
  return typeof raw === 'string' && raw.trim().toLowerCase() === 'true';
}

/** Session replay kill switch. Default: recording on whenever analytics are enabled. */
function isReplayDisabled(): boolean {
  const raw = import.meta.env.VITE_POSTHOG_DISABLE_REPLAY;
  return typeof raw === 'string' && raw.trim().toLowerCase() === 'true';
}

export function isAnalyticsEnabled(): boolean {
  return enabled;
}

export function initAnalytics(): void {
  if (enabled) return;
  if (typeof window === 'undefined') return;
  const key = import.meta.env.VITE_POSTHOG_KEY?.trim();
  if (!key) return;

  try {
    posthog.init(key, {
      // The myKOL PostHog project lives on EU Cloud; events sent to the US host are silently dropped.
      api_host: import.meta.env.VITE_POSTHOG_HOST?.trim() || 'https://eu.i.posthog.com',
      defaults: '2025-05-24',
      capture_pageview: 'history_change',
      capture_pageleave: true,
      autocapture: true,
      persistence: 'localStorage+cookie',
      person_profiles: 'identified_only',
      // Session replay (https://posthog.com/docs/session-replay/start-here). Recording starts
      // automatically once "Record user sessions" is on in the PostHog project settings; the
      // project-side sampling / privacy rules apply on top of this client config.
      disable_session_recording: isReplayDisabled(),
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '[data-ph-mask]',
        // Canvas recording stays off (default) — canvases can render documents/signatures.
        // posthog-js never records cross-origin iframes unless this is true; keep it explicit.
        recordCrossOriginIframes: false,
      },
    });
    enabled = true;
    // Error tracking (https://posthog.com/docs/error-tracking). Unhandled errors and
    // rejections are only sent when "Exception autocapture" is also on in the PostHog
    // project settings (Error tracking → Settings); this call turns on the client side.
    try {
      posthog.startExceptionAutocapture();
    } catch {
      /* never let analytics break the app */
    }
  } catch (err) {
    // Never let analytics break the app.
    console.warn('[analytics] init failed', err);
    enabled = false;
  }
}

export function track(event: ClientEvent, props?: EventProps): void {
  if (!enabled) return;
  try {
    posthog.capture(event, props);
  } catch {
    /* ignore */
  }
}

export function identifyUser(user: IdentityUser): void {
  if (!enabled) return;
  try {
    const payload = buildIdentifyPayload(user, { sendEmail: sendEmailEnabled() });
    posthog.identify(payload.distinctId, payload.person);
    posthog.register(payload.superProps);
  } catch {
    /* ignore */
  }
}

export function resetAnalytics(): void {
  if (!enabled) return;
  try {
    posthog.unregister('role');
    posthog.unregister('org_id');
    posthog.unregister('org_type');
    posthog.reset();
  } catch {
    /* ignore */
  }
}

/**
 * Manually report an error to PostHog error tracking.
 * No-op unless analytics are enabled. Unhandled errors/rejections are already
 * covered by `startExceptionAutocapture`; use this for caught errors that would
 * otherwise be invisible (API 5xx, failed uploads, swallowed catch blocks).
 */
export function captureError(error: unknown, props?: Record<string, unknown>): void {
  if (!enabled) return;
  try {
    (posthog.captureException as (e: unknown, p?: Record<string, unknown>) => void)(error, props);
  } catch {
    /* ignore */
  }
}

/** Current PostHog session id, for linking backend logs to this replay. Null when unavailable. */
export function getPostHogSessionId(): string | null {
  if (!enabled) return null;
  try {
    const id = posthog.get_session_id();
    return typeof id === 'string' && id ? id : null;
  } catch {
    return null;
  }
}

/** Current PostHog distinct id, for linking backend logs to this person. Null when unavailable. */
export function getPostHogDistinctId(): string | null {
  if (!enabled) return null;
  try {
    const id = posthog.get_distinct_id();
    return typeof id === 'string' && id ? id : null;
  } catch {
    return null;
  }
}

/**
 * Send a structured log to PostHog logs (https://posthog.com/docs/logs).
 * `distinct_id` / `session_id` are attached automatically by posthog-js, so a
 * frontend log links to the person and replay without extra work.
 */
export function logToPostHog(
  level: 'info' | 'warn' | 'error',
  message: string,
  props?: Record<string, string | number | boolean | null>
): void {
  if (!enabled) return;
  try {
    posthog.captureLog({ level, body: message, attributes: props });
  } catch {
    /* ignore */
  }
}

/** True while the current tab is actively recording a session replay. */
export function isSessionReplayRecording(): boolean {
  if (!enabled) return false;
  try {
    return posthog.sessionRecordingStarted() === true;
  } catch {
    return false;
  }
}

/** Playback URL of the current session replay, or null when unavailable. */
export function getSessionReplayUrl(withTimestamp = false): string | null {
  if (!enabled) return null;
  try {
    const url = posthog.get_session_replay_url(
      withTimestamp ? { withTimestamp: true } : undefined
    );
    return typeof url === 'string' && url ? url : null;
  } catch {
    return null;
  }
}

let unsubscribe: (() => void) | null = null;

/**
 * Subscribe once to the auth store. The store sets `user` from eight
 * different actions, so keying on `identityKey` here is simpler and safer
 * than instrumenting each one. Re-identifies when the org appears after
 * onboarding; resets on logout.
 */
export function bindAnalyticsToAuth(): void {
  if (unsubscribe) return;
  let lastKey: string | null = null;

  const apply = (user: IdentityUser | null | undefined) => {
    const key = identityKey(user);
    if (key === lastKey) return;
    lastKey = key;
    if (user) identifyUser(user);
    else resetAnalytics();
  };

  apply(useAuthStore.getState().user);
  unsubscribe = useAuthStore.subscribe((state) => apply(state.user));
}
